import { ExclamationCircleFilled } from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Form, Modal } from "antd";
import { RightDrawer } from "@/components";
import dayjs from "@/utils/dayjsConfig";
import { useOmsDatePicker } from "@/hooks";
import { updateOmsSchedule, getOmsDetail, postSwitchOn } from "@api";
import {
  ProFormText,
  ProForm,
  ProFormTextArea,
} from "@ant-design/pro-components";
import useMessageRedux from "@/store/message-redux";
import { OMS_FORMAT } from "@/enums/pageEnum";
import {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useTransition,
} from "react";

export default function OMSScheduleDetailPage() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const navigation = useNavigate();
  const { id } = useParams();
  const [isPending, startTransition] = useTransition();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [data, setData] = useState<any>();
  const { getOfftime, getOntime, setOffTime, setOnTime } = useOmsDatePicker();
  const { message } = useMessageRedux();

  const getDetail = useCallback(() => {
    startTransition(async () => {
      try {
        const res = await getOmsDetail(id);
        const body = res.data?.body;
        form?.setFieldsValue({
          ...body,
          offAtFormat: dayjs(body?.offAt).format(OMS_FORMAT),
          onAtFormat: dayjs(body?.onAt).format(OMS_FORMAT),
          delay: body?.delay || "",
          duration: body?.duration || "",
          closedAtFormat: body?.closedAt
            ? dayjs(body?.closedAt).format(OMS_FORMAT)
            : "",
        });
        setOffTime(dayjs(body?.offAt));
        setOnTime(dayjs(body?.onAt));
        setData(body);
      } catch (error: any) {
        message.error(error?.detail || error);
      }
    });
  }, [form, id, message, setOffTime, setOnTime]);

  useEffect(() => {
    getDetail();
  }, [getDetail]);

  const getFormText = useCallback(
    (name: string, label?: string) => {
      const labText = t(label || name);
      return <ProFormText name={name} label={labText} placeholder={""} />;
    },
    [t]
  );

  const readOnly = useMemo(() => {
    if (data?.status !== "CLOSED") return true;
  }, [data]);

  const getTimeFrom = useMemo(() => {
    const cTime = new Date();
    if (isEdit && cTime.valueOf() < data?.offAt) {
      return (
        <>
          {getOfftime}
          {getOntime}
        </>
      );
    }
    return (
      <>
        <ProFormText name="offAtFormat" label={t("schOff")} placeholder={""} />
        <ProFormText name="onAtFormat" label={t("schOn")} placeholder={""} />
      </>
    );
  }, [t, isEdit, getOfftime, getOntime, data]);

  const getStatus = useMemo(() => {
    let colorStatus = "";
    if (data?.statusFormat === "Open") {
      colorStatus = "bg-[#1890ff1a] text-[#1890ff]";
    } else if (data?.statusFormat === "Closed") {
      colorStatus =
        "bg-[#F0F0F0] dark:bg-[#F0F0F01a] text-[#00000073] dark:text-[#ffffff73]";
    } else {
      colorStatus = "bg-[#ff55001a] text-[#FF5500]";
    }
    return (
      <div
        className={`${colorStatus} h-8 px-5 mt-4 mb-6 text-[16px] font-medium leading-8`}
      >
        {t(data?.statusFormat)}
      </div>
    );
  }, [data, t]);

  const switchDisabled = useMemo(() => {
    const cTime = new Date();
    if (cTime.valueOf() < data?.offAt) return true;
    if (data?.status !== "CLOSED") return false;
    return true;
  }, [data]);

  return (
    <RightDrawer
      styles={{ body: { padding: "0px" } }}
      title={t("maintDetail")}
      loading={isPending}
      width={500}
      className="omsScheduleClass"
      drawerTitle={<p className="pl-5">{t("maintDetail")}</p>}
      footer={
        readOnly && (
          <div className="text-right">
            {isEdit && (
              <Button variant="outlined" onClick={() => setIsEdit(false)}>
                {t("cancel")}
              </Button>
            )}
            <Button
              type="primary"
              loading={isPending}
              className="pr-4 pl-4 ml-3"
              onClick={() => {
                if (isEdit) {
                  form?.submit?.();
                } else {
                  setIsEdit(true);
                }
              }}
            >
              {isEdit ? t("save") : t("edit")}
            </Button>
          </div>
        )
      }
    >
      {getStatus}
      <ProForm
        layout={"horizontal"}
        form={form}
        disabled={true}
        className="px-5 oms-right-drawer formDisabledColor"
        submitter={false}
        onFinish={(values) => {
          Modal.confirm({
            title: "Save",
            icon: <ExclamationCircleFilled />,
            content: "Do you want save this item?",
            className: "loginModalName",
            onOk() {
              startTransition(async () => {
                try {
                  const params: any = {
                    id: data?.id,
                    report: values?.report,
                    liftId: data?.liftId,
                  };
                  if (new Date().valueOf() < data?.offAt) {
                    params.offAt = dayjs(values.offAt).format();
                    params.onAt = dayjs(values.onAt).format();
                  }
                  await updateOmsSchedule(params);
                  navigation(-1);
                } catch (error: any) {
                  message.error(error?.detail || error);
                }
              });
            },
          });
        }}
      >
        {getFormText("liftName", "lift")}
        {getFormText("buildingAddress", "address")}
        {getFormText("liftCompany", "LIFT_COMPANY")}
        {getFormText("tel", "Tel")}
        <hr className="text-[#E8E9ED] dark:text-[#303030] my-6" />
        <div className="flex justify-between items-center gap-6">
          <div className="flex-1">
            {getTimeFrom}
            {getFormText("closedAtFormat", "actualTime")}
            {getFormText("duration")}
            {getFormText("delay")}
          </div>
          {!isEdit && !switchDisabled && !data?.switchOnAt && (
            <div>
              <Button
                color="primary"
                variant="outlined"
                className="ml-3"
                disabled={switchDisabled}
                onClick={() => {
                  startTransition(async () => {
                    try {
                      await postSwitchOn(data?.id);
                      getDetail();
                    } catch (error: any) {
                      message.error(error?.detail || error);
                    }
                  });
                }}
              >
                {t("switchOn")}
              </Button>
            </div>
          )}
        </div>
        <hr className="text-[#E8E9ED] dark:text-[#303030] my-6" />
        <div>
          <p className="flex justify-start gap-1">
            <span className="text-[#f00] text-[18px]">*</span>
            <span>{t("reportDescr")}</span>
          </p>
          <ProFormTextArea
            name="report"
            disabled={!isEdit}
            label=""
            rules={[
              { required: true, message: "Please input report description." },
            ]}
            placeholder={""}
            fieldProps={{ maxLength: 2000, showCount: true }}
          />
        </div>
      </ProForm>
    </RightDrawer>
  );
}
